/**
 * codeflow
 */
/* eslint-disable camelcase */
import { checkBrowser, isMobile } from '@/utils';
import { setToken } from '@/utils/fetch';
import getIsNoNeedSign from '@/utils/getIsNoNeedSign';
import getQuery from '@/utils/getQuery';
import goAuthorizeHtml from '@/utils/goAuthorizeHtml';
import {
  generateCodeChallenge,
  generateCodeVerifier,
  getCodeVerifier,
  saveCodeVerifier,
} from '@/utils/pkce';
import { getAuthURLByPlatform } from '@/utils/platform';
import { assertQuery, deleteUrlCode } from '@/utils/urlUtils';
import * as queryString from 'query-string';
import { useEffect } from 'react';

/**
 * 從 Url 獲取 code 參數
 */
const getCodeFormUrl = () => {
  const query = getQuery();
  return assertQuery(query.code as string);
};

/**
 * 跳轉到 {一戶通/商社通}/auth.html
 */
const handleGoLoginHtml = () => {
  const query = queryString.parse(window.location.search);

  // 授权失败
  if (query.error) {
    console.error(`授权失败：${location.href}`);
    return;
  }
  const { language, accountType, state } = query;
  const code_verifier = generateCodeVerifier();
  saveCodeVerifier(code_verifier);

  // auth.html 會對 redirectUri 進行處理，會判斷是否需補充 accountType 參數
  const url = queryString.stringifyUrl({
    url: `${getAuthURLByPlatform()}/auth.html`,
    // 有值的項才會添加到 url 中
    query: {
      appId: GOV_MO.APP_ID,
      client_id: GOV_MO.CLIENT_ID,
      entity_client_id: GOV_MO.ENTITY_ID,
      scope: 'profile',
      grantType: 'authorization_code',
      redirectUri: window.location.href,
      language,
      accountType,
      state,
      code_challenge: generateCodeChallenge(getCodeVerifier()),
    },
  });

  window.location.href = url;
};

interface UseOAuth2Props {
  handleCode?: (code: string) => unknown;
}

const useOAuth2 = (props: UseOAuth2Props) => {
  const { handleCode } = props;

  /**
   * PC端授權流程
   */
  const handlePCOAuth2 = () => {
    const code = getCodeFormUrl();
    // 無 code, 跳轉授權頁面
    if (!code) {
      handleGoLoginHtml();
      return;
    }
    deleteUrlCode();
    handleCode?.(code);
  };

  /**
   * App端授權流程
   */
  const handleAppOAuth2 = async () => {
    // const code = await getCodeByJsApi();
    const code = getCodeFormUrl();
    // 無 code, 跳轉授權頁面
    if (!code) {
      goAuthorizeHtml();
      return;
    }
    deleteUrlCode();
    handleCode?.(code);
  };

  const handleOAuth2 = () => {
    if (isMobile()) {
      handleAppOAuth2();
      return;
    }
    // PC 授權
    handlePCOAuth2();
  };

  useEffect(() => {
    if (!checkBrowser()) return;
    if (getIsNoNeedSign()) return;
    // 在本地环境的时候，mock登录
    if (process.env.NODE_ENV === 'development') {
      // 本地模拟pkce
      const code_verifier = generateCodeVerifier();
      saveCodeVerifier(code_verifier);
      handleCode?.('123456');
    } else {
      handleOAuth2();
    }
  }, []);
};

export default useOAuth2;
